import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';

export interface InfoTooltipProps {
  /**
   * Accessible name for the trigger. Must identify what it describes — a page
   * with two info icons read as "info, info" is useless to a screen reader.
   * e.g. "More information about Maximum Deductible".
   */
  label: string;
  /** Tooltip body text. */
  text: string;
}

interface Position {
  top: number;
  left: number;
}

interface Box {
  width: number;
  height: number;
}

interface TriggerBox {
  top: number;
  bottom: number;
  left: number;
  width: number;
}

/** Gap between the trigger and the bubble, and the minimum inset from any viewport edge. */
const EDGE_MARGIN = 8;

/** Horizontal padding inside the bubble, per side. Must match the inline style below. */
const BUBBLE_PADDING_X = 10;

/** Widest the bubble may get before its text wraps. */
const BUBBLE_MAX_WIDTH = 260;

/**
 * Width of the longest line box, given the client rects of a text range.
 *
 * A box with `max-width` that wraps is laid out at exactly `max-width`, even
 * when every wrapped line is shorter than that — which leaves dead space to the
 * right of the text. Measuring the line boxes lets the bubble hug its longest
 * line instead. There is no pure-CSS equivalent.
 *
 * Returns 0 when there is nothing to measure, which callers treat as "leave the
 * width alone" (jsdom produces no rects).
 */
export function widestLineWidth(rects: ArrayLike<{ width: number }>): number {
  let widest = 0;
  for (let i = 0; i < rects.length; i += 1) {
    const { width } = rects[i];
    if (width > widest) {
      widest = width;
    }
  }
  return widest;
}

/**
 * Places the bubble below the trigger, horizontally centered but clamped inside
 * the viewport, flipping above the trigger when there is no room below.
 *
 * Centering alone is not enough: an icon near the right edge of a narrow
 * viewport pushes half the bubble off-screen, where the window clips it.
 *
 * Exported for direct unit testing — jsdom reports every element rect as zero,
 * so this arithmetic cannot be exercised through the rendered component.
 */
export function computeTooltipPosition(
  trigger: TriggerBox,
  bubble: Box,
  viewport: Box,
  margin: number = EDGE_MARGIN
): Position {
  const center = trigger.left + trigger.width / 2;
  const rightmost = Math.max(margin, viewport.width - bubble.width - margin);
  const left = Math.min(Math.max(margin, center - bubble.width / 2), rightmost);

  let top = trigger.bottom + margin;
  if (top + bubble.height > viewport.height - margin) {
    const above = trigger.top - bubble.height - margin;
    if (above >= margin) {
      top = above;
    }
  }

  return { top, left };
}

/**
 * Whether a pointer device should trigger hover-to-open.
 *
 * Touch and pen fire pointer enter before click, which would open the tooltip
 * and then have the click immediately toggle it shut. Excluding those rather
 * than requiring 'mouse' is deliberate: an environment that does not report
 * pointerType then degrades to hover-capable desktop behavior instead of
 * silently losing hover.
 *
 * Exported for direct unit testing — jsdom cannot represent pointerType on a
 * synthetic event, so this branch is untestable through the rendered component.
 */
export function isHoverPointer(pointerType: string): boolean {
  return pointerType !== 'touch' && pointerType !== 'pen';
}

/**
 * Accessible info icon with a tooltip (DAED-271).
 *
 * The trigger is a real <button> rather than the legacy `<img class="information-icon">`
 * pattern, so it is reachable by keyboard and announced as interactive.
 *
 * The bubble is positioned `fixed` from the trigger's bounding rect rather than
 * `absolute`. The accumulator table sits inside `.panel.table-responsive`, and
 * Bootstrap 3 gives that `overflow-x: auto` at narrow widths, which clips
 * absolutely-positioned descendants. Fixed positioning escapes the overflow
 * context entirely, so the tooltip survives the mobile layout.
 *
 * Styling is inline rather than in a stylesheet: there are five white-label
 * stylesheets and no shared place to put component CSS, so inline keeps every
 * brand consistent without touching files other pages depend on.
 */
export function InfoTooltip({ label, text }: InfoTooltipProps) {
  const tooltipId = useId();
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<Position | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const bubbleRef = useRef<HTMLSpanElement>(null);

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    const bubble = bubbleRef.current;
    if (!trigger || !bubble) {
      return;
    }

    // Shrink the bubble to its longest wrapped line before measuring it, so the
    // clamped position is computed against the width it will actually paint at.
    // Done imperatively rather than through state to keep this a single
    // pre-paint pass.
    bubble.style.width = '';
    if (typeof document.createRange === 'function') {
      const range = document.createRange();
      range.selectNodeContents(bubble);
      // jsdom implements createRange but not getClientRects, so probe the method
      // rather than assuming a Range carries it.
      const widest =
        typeof range.getClientRects === 'function'
          ? widestLineWidth(range.getClientRects())
          : 0;
      if (widest > 0) {
        bubble.style.width = `${Math.ceil(widest) + BUBBLE_PADDING_X * 2}px`;
      }
    }

    const triggerRect = trigger.getBoundingClientRect();
    const bubbleRect = bubble.getBoundingClientRect();

    setPosition(
      computeTooltipPosition(
        {
          top: triggerRect.top,
          bottom: triggerRect.bottom,
          left: triggerRect.left,
          width: triggerRect.width,
        },
        { width: bubbleRect.width, height: bubbleRect.height },
        { width: window.innerWidth, height: window.innerHeight }
      )
    );
  }, []);

  useLayoutEffect(() => {
    if (open) {
      // Runs before paint, so the measure-then-place pass is never visible.
      updatePosition();
    } else {
      // Drop the stale position so a reopen re-measures rather than flashing
      // the bubble at wherever it sat last time.
      setPosition(null);
    }
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) {
      return;
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    }

    function onPointerDown(event: Event) {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target) || bubbleRef.current?.contains(target)) {
        return;
      }
      setOpen(false);
    }

    function onReflow() {
      updatePosition();
    }

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    window.addEventListener('scroll', onReflow, true);
    window.addEventListener('resize', onReflow);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      window.removeEventListener('scroll', onReflow, true);
      window.removeEventListener('resize', onReflow);
    };
  }, [open, updatePosition]);

  function handlePointerEnter(event: React.PointerEvent<HTMLButtonElement>) {
    if (isHoverPointer(event.pointerType)) {
      setOpen(true);
    }
  }

  function handlePointerLeave(event: React.PointerEvent<HTMLButtonElement>) {
    if (isHoverPointer(event.pointerType)) {
      setOpen(false);
    }
  }

  return (
    <span style={{ display: 'inline-block', position: 'relative' }}>
      <button
        type="button"
        ref={triggerRef}
        aria-label={label}
        // Deliberately no aria-expanded: per the ARIA Authoring Practices that
        // belongs to disclosure and combobox patterns. On a tooltip trigger it
        // makes the button announce "collapsed"/"expanded" for a state the user
        // cannot act on. aria-describedby is the whole contract here.
        aria-describedby={open ? tooltipId : undefined}
        onPointerEnter={handlePointerEnter}
        onPointerLeave={handlePointerLeave}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onClick={() => setOpen(current => !current)}
        style={{
          background: 'none',
          border: 'none',
          padding: 0,
          margin: '0 0 0 4px',
          cursor: 'pointer',
          verticalAlign: 'middle',
          lineHeight: 1,
          color: 'inherit',
        }}
      >
        <i className="material-icons" aria-hidden="true" style={{ fontSize: 16 }}>
          info
        </i>
      </button>

      {open && (
        <span
          id={tooltipId}
          ref={bubbleRef}
          role="tooltip"
          style={{
            position: 'fixed',
            top: position?.top ?? 0,
            left: position?.left ?? 0,
            // Hidden for the single pre-paint frame where the bubble has been
            // rendered for measurement but not yet placed.
            visibility: position ? 'visible' : 'hidden',
            zIndex: 1070,
            maxWidth: BUBBLE_MAX_WIDTH,
            // Explicit, because the surrounding Bootstrap sets border-box
            // globally and the measured width above assumes padding is included.
            boxSizing: 'border-box',
            padding: `8px ${BUBBLE_PADDING_X}px`,
            borderRadius: 4,
            background: '#333',
            color: '#fff',
            fontSize: 12,
            fontWeight: 'normal',
            lineHeight: 1.4,
            textAlign: 'left',
            textTransform: 'none',
            whiteSpace: 'normal',
            boxShadow: '0 2px 6px rgba(0, 0, 0, 0.3)',
          }}
        >
          {text}
        </span>
      )}
    </span>
  );
}
